import React, { useEffect, useRef, useState } from "react";
import Spinner from "../../components/spinner/spinner";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import styles from "./PurchaseOrderCreation.module.scss";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { getPurchaseOrderDetails } from "../../services/PurchaseOrderCreationService";
import * as constants from "../../constants/constants";
import {
  formatDateAndTimeFromUtcToPst,
  emailOrdersD365StatusOptions
} from "../../constants/constants";
import { Tag } from "primereact/tag";
import {
  purchaseOrderColumns,
  purchaseOrderLineItemsColumns
} from "../../constants/table-columns/tableColumns";

const PurchaseOrderCreation = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);
  const [
    purchaseOrderContentModalVisible,
    setPurchaseOrderContentModalVisible
  ] = useState(false);
  const [purchaseOrderContent, setPurchaseOrderContent] = useState(null);

  const toast = useRef(null);
  useEffect(() => {
    fetchPurchaseOrderDetails();
     window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchPurchaseOrderDetails = async () => {
    try {
      setShowSpinner(true);
      let result = await getPurchaseOrderDetails();
      setPurchaseOrder(result);
    } catch (error) {
      setShowSpinner(false);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    } finally {
      setShowSpinner(false);
    }
  };

  const onHidePurchaseOrderContentModal = () => {
    if (purchaseOrderContent?.startsWith("blob:")) {
      URL.revokeObjectURL(purchaseOrderContent);
    }
    setPurchaseOrderContent(null);
    setPurchaseOrderContentModalVisible(false);
  };

  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-1 pl-5 ml-5">
        <DataTable
          value={data?.lineItems}
          scrollable
          paginator={constants.paginator}
          rows={constants.defaultRows}
          rowsPerPageOptions={constants.rowsPerPageOptions}
          paginatorTemplate={constants.paginatorTemplate}
          currentPageReportTemplate={constants.currentPageReportTemplate}
          emptyMessage={constants.emptyMessage}
        >
          {purchaseOrderLineItemsColumns.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sort}
              style={{ minWidth: col.minWidth }}
              body={col?.value}
              headerStyle={{ minWidth: col.minWidth }}
            />
          ))}
        </DataTable>
      </div>
    );
  };

  const toggleRows = () => {
    if (allExpanded) {
      setExpandedRows(null);
      setAllExpanded(false);
    } else {
      let newExpandedRows = {};
      purchaseOrder.forEach(
        (p) => (newExpandedRows[`${p?.id}`] = true)
      );
      setExpandedRows(newExpandedRows);
      setAllExpanded(true);
    }
  };

  const triggerDocumentViewHandler = (blobPath) => {
    if (blobPath) {
      setPurchaseOrderContent(blobPath);
      setPurchaseOrderContentModalVisible(true);
    }
  };

  const viewDocumentHandler = (rowData) => {
    try {
      return (
        <Button
          icon="pi pi-file"
          rounded
          text
          aria-label="View Document"
          severity="info"
          className={styles.contentBtn}
          onClick={() => triggerDocumentViewHandler(rowData?.blobpath)}
        />
      );
    } catch (error) {
      setShowSpinner(false);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    }
  };

  const emailReceivedTimeFormat = (rowData) => {
    return formatDateAndTimeFromUtcToPst(rowData.emailRecievedDateTime);
  };

  const d365StatusFormat = (rowData) => {
    let severityValue = "";
    if (!rowData?.d365Status?.trim()) return;
    switch (rowData.d365Status) {
      case emailOrdersD365StatusOptions.Enqueued:
        severityValue = "secondary";
        break;
      case emailOrdersD365StatusOptions.InProcess:
        severityValue = "info";
        break;
      case emailOrdersD365StatusOptions.ProcessedWithErrors:
        severityValue = "danger";
        break;
      case emailOrdersD365StatusOptions.Processed:
        severityValue = "success";
        break;
      default:
        severityValue = "warning";
        break;
    }

    return (
      <div>
        <Tag severity={severityValue} value={rowData.d365Status} rounded></Tag>
      </div>
    );
  };

  const actionTakenDateUTCFormat = (rowData) => {
    return formatDateAndTimeFromUtcToPst(rowData?.actionTakenDateUTC);
  };

  return (
    <>
      {showSpinner && <Spinner />}
      <Toast ref={toast} />
      <Dialog
        visible={purchaseOrderContentModalVisible}
        modal
        header="Document Viewer"
        style={{ width: "70vw", height: "80vh" }}
        breakpoints={{ "1024px": "75vw", "641px": "100vw" }}
        onHide={onHidePurchaseOrderContentModal}
        maximizable
      >
        {purchaseOrderContent
          ? (() => {
            const lowerPath = purchaseOrderContent.toLowerCase();
            const isExcel =
              lowerPath.endsWith(".xlsx") ||
              lowerPath.endsWith(".xls") ||
              lowerPath.endsWith(".csv") ||
              lowerPath.endsWith(".doc") ||
              lowerPath.endsWith(".docx");

            if (isExcel) {
              return (
                <div style={{ padding: "2rem" }}>
                  <p>This file type cannot be previewed in the browser.</p>
                  <a
                    href={purchaseOrderContent}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click here to download the file
                  </a>
                </div>
              );
            }
            const isEmailBody =
              purchaseOrderContent.startsWith("<") &&
              purchaseOrderContent.endsWith(">");

            if (isEmailBody) {
              return (
                <div
                  style={{
                    overflowY: "auto",
                    height: "100%",
                    padding: "1rem"
                  }}
                  dangerouslySetInnerHTML={{ __html: purchaseOrderContent }}
                />
              );
            }

            return (
              <iframe
                src={purchaseOrderContent}
                title="File Viewer"
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            );
          })()
          : null}
      </Dialog>

      <div className="mt-4 pt-5">
        <DataTable
          value={purchaseOrder}
          tableStyle={{ minWidth: "50rem" }}
          scrollable
          paginator={constants.paginator}
          rows={constants.defaultRows}
          rowsPerPageOptions={constants.rowsPerPageOptions}
          paginatorTemplate={constants.paginatorTemplate}
          currentPageReportTemplate={constants.currentPageReportTemplate}
          emptyMessage={constants.emptyMessage}
          dataKey="id"
          scrollHeight="30vw"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e?.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column
            header={
              <Button
                className={
                  allExpanded ? "pi pi-angle-down" : "pi pi-angle-right"
                }
                style={{
                  fontSize: "1.3rem",
                  color: "#6c757d",
                  backgroundColor: "#f8f9fa",
                  border: "none",
                  padding: "2px"
                }}
                onClick={toggleRows}
              ></Button>
            }
            style={{ width: "10px" }}
            expander={true}
          />

          {purchaseOrderColumns.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sort}
              style={{ minWidth: col.minWidth }}
              headerStyle={{ minWidth: col.minWidth }}
              body={
                col.field.toLocaleLowerCase() === "emailrecieveddatetime"
                  ? emailReceivedTimeFormat
                  : col.field.toLocaleLowerCase() === "d365status"
                    ? d365StatusFormat
                    : col.field.toLocaleLowerCase() === "actiontakendateutc"
                      ? actionTakenDateUTCFormat
                      : col.value
              }
            />
          ))}
        </DataTable>
      </div>
    </>
  );
};
export default PurchaseOrderCreation;
